import { Spin } from 'antd';
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { getDevIdentity, INTENDED_ROUTE_KEY, isDevLoginEnabled } from './devLogin';
import { TabLimitBlocker } from './TabLimitBlocker';
import { useSession } from './useSession';
import { useSessionTimer } from './useSessionTimer';
import { useTabSlot } from './useTabSlot';

type Props = {
  children: ReactNode;
};

const spinner = (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
    <Spin size="large" />
  </div>
);

/**
 * Guards a subtree by requiring a valid server-side session. Unauthenticated
 * requests are sent to the BFF Entra SSO login at GET /auth/login.
 *
 * Also the app's single gate on how many tabs may run at once (see tabRegistry.ts), for the
 * same reason it hosts the session timer: it wraps every protected route, so one mount here
 * covers the whole app.
 *
 * In Dev Login mode (localhost + Dev only) session checks are bypassed and the user
 * is sent to the role-picker screen if no identity has been picked yet.
 */
export function AuthGuard({ children }: Props) {
  const location = useLocation();
  const devLogin = isDevLoginEnabled();
  const { user, loading } = useSession();
  const tab = useTabSlot();

  // Mounted unconditionally (before any early return below) per Rules of Hooks; the
  // hook itself no-ops until `user` resolves. AuthGuard wraps every protected route, so
  // this is the single natural place to run the session-refresh timer for the whole app.
  // Only the leader tab drives the refresh interval — see useSessionTimer.
  useSessionTimer(
    user
      ? {
          accessTokenExpiresAt: user.accessTokenExpiresAt,
          sessionExpiresAt: user.sessionExpiresAt,
          refreshIntervalSeconds: user.refreshIntervalSeconds,
          refreshTokenExpiresAt: user.refreshTokenExpiresAt,
        }
      : null,
    tab.isLeader,
  );

  if (tab.state === 'claiming') return spinner;

  // The user came back to a tab that had already lost its slot. Unlike `evicted` this DOES
  // unmount the tree — and with it any advisor answers FormShell was holding — so it is
  // reached only from an explicit return to a dead tab, never from the eviction itself.
  // /auth/error sits outside this guard, so routing there also releases the slot on unmount.
  if (tab.state === 'expired') {
    return <Navigate to="/auth/error?cause=session-timeout" replace />;
  }

  // Ahead of the login redirect below, deliberately: a tab that isn't allowed to run must not
  // be the reason a user gets bounced through ISAM.
  if (tab.state === 'denied') return <TabLimitBlocker variant="denied" onRetry={tab.retry} />;

  if (devLogin) {
    if (!getDevIdentity()) {
      sessionStorage.setItem(INTENDED_ROUTE_KEY, location.pathname + location.search);
      return <Navigate to="/auth/dev-login" replace />;
    }
    return <>{children}</>;
  }

  if (loading) {
    return spinner;
  }

  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    window.location.href = `/auth/login?returnUrl=${returnUrl}`;
    return spinner;
  }

  // `evicted` overlays rather than replaces: this tree may hold advisor answers that live
  // nowhere but React memory, and unmounting it to show a notice would destroy them.
  return (
    <>
      {children}
      {tab.state === 'evicted' && <TabLimitBlocker variant="evicted" onRetry={tab.retry} />}
    </>
  );
}
